import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cytoscape, { type Core, type ElementDefinition, type NodeSingular, type StylesheetCSS } from 'cytoscape';
import fcose from 'cytoscape-fcose';
import { CaretDown, FrameCorners, MagnifyingGlassPlus, MagnifyingGlassMinus, Target, X } from '@phosphor-icons/react';
import { useGraphSubgraph } from '~/hooks/queries/use-graph';
import { useTheme } from '~/lib/stores/theme-store';
import { Tip } from '~/components/ui/tip';

cytoscape.use(fcose);

const ONE_DAY = 24 * 60 * 60 * 1000;

interface ConstellationTheme {
  edgeColor: string;
  edgeOpacity: number;
  accent: string;
  glowOpacity: number;
  cPerson: string;
  cCompany: string;
  cProject: string;
  cEvent: string;
  cPreference: string;
  cRoutine: string;
}

/**
 * Resolve constellation tokens from CSS custom properties on `<html>`.
 * Cytoscape can't parse `var(--x)`, so we have to read computed values and
 * push raw colours into its stylesheet. Called on mount and on every theme
 * change so light↔dark swaps recolor without a re-render.
 */
function readConstellationTheme(): ConstellationTheme {
  const cs = getComputedStyle(document.documentElement);
  const v = (name: string, fallback: string) => (cs.getPropertyValue(name) || fallback).trim();
  const edgeRgb = v('--cnstl-edge', '28, 29, 43');
  return {
    edgeColor: `rgba(${edgeRgb}, 1)`,
    edgeOpacity: parseFloat(v('--cnstl-edge-opacity', '0.14')) || 0.14,
    accent: v('--cnstl-accent', '#2f54e8'),
    glowOpacity: parseFloat(v('--cnstl-glow-opacity', '0')) || 0,
    cPerson: v('--c-person', '#6b4ec7'),
    cCompany: v('--c-company', '#1e6fa8'),
    cProject: v('--c-project', '#0e7c6f'),
    cEvent: v('--c-event', '#b8884a'),
    cPreference: v('--c-preference', '#a82e9a'),
    cRoutine: v('--c-routine', '#64748b'),
  };
}

// FRONT-2378: `routine` retired as a graph entity kind (every Work container
// is a `project` now) — `cRoutine`/`--c-routine` survive as the shared
// "unknown/other kind" fallback color token (also the default this
// function itself falls through to), not as a real kind's own color.
function colorForType(type: string, t: ConstellationTheme): string {
  switch (type) {
    case 'person': return t.cPerson;
    case 'company': return t.cCompany;
    case 'project': return t.cProject;
    case 'event': return t.cEvent;
    case 'preference': return t.cPreference;
    default: return t.cRoutine;
  }
}

/**
 * Map an entity's last-touched timestamp to an alpha in [0.30, 1.0]. Fresh
 * (<1d) glows; stale (>180d) fades to the faintest readable level.
 */
function recencyAlpha(updatedAt?: string | null): number {
  if (!updatedAt) return 0.4;
  const ageMs = Date.now() - new Date(updatedAt).getTime();
  if (Number.isNaN(ageMs)) return 0.4;
  const days = ageMs / ONE_DAY;
  if (days < 1) return 1.0;
  if (days < 7) return 0.85;
  if (days < 30) return 0.65;
  if (days < 180) return 0.45;
  return 0.30;
}

/** Cytoscape stylesheet builder — tiny luminous dots, no native labels. */
function constellationStyleSpec(t: ConstellationTheme): StylesheetCSS[] {
  // Halo width/opacity differ per theme: dark mode glows, light mode whispers.
  const haloPadding = t.glowOpacity > 0 ? 6 : 4;
  const haloAlpha = t.glowOpacity > 0 ? 0.32 : 0.14;
  return ([
    {
      selector: 'node',
      style: {
        'background-color': 'data(color)',
        'background-opacity': 1,
        label: '',
        width: 5,
        height: 5,
        'border-width': 0,
        'overlay-color': 'data(color)',
        'overlay-padding': haloPadding,
        'overlay-opacity': haloAlpha,
        'overlay-shape': 'ellipse',
        'transition-property':
          'width, height, border-width, border-color, overlay-padding, overlay-opacity',
        'transition-duration': '180ms',
        'z-index': 10,
      },
    },
    {
      selector: 'node[isFocal = "yes"]',
      style: {
        width: 10,
        height: 10,
        'border-width': 0.6,
        'border-color': t.accent,
        'border-opacity': 0.55,
        'overlay-padding': haloPadding + 4,
        'z-index': 20,
      },
    },
    {
      selector: 'node[isPinned = "yes"]',
      style: { 'border-width': 0.4, 'border-color': t.accent, 'border-opacity': 0.4 },
    },
    {
      selector: 'node:selected',
      style: { 'border-width': 0.6, 'border-color': t.accent, 'border-opacity': 0.55 },
    },
    {
      selector: 'edge',
      style: {
        width: 0.6,
        'line-color': t.edgeColor,
        opacity: t.edgeOpacity,
        'curve-style': 'bezier',
        label: '',
        'transition-property': 'line-color, opacity, width',
        'transition-duration': '180ms',
        'z-index': 1,
      },
    },
    {
      selector: 'edge.highlighted',
      style: { width: 0.9, 'line-color': t.accent, opacity: 0.7, 'z-index': 5 },
    },
    {
      selector: 'edge[fresh = "yes"]',
      style: { 'line-color': t.accent, 'line-style': 'dashed', opacity: 1 },
    },
  ] as unknown) as StylesheetCSS[];
}

/**
 * Tear down any stale per-node label divs and create fresh ones for the
 * current Cytoscape graph. Labels are absolutely positioned children of the
 * overlay; positioning happens in `positionOverlayLabels` on every render.
 */
function rebuildOverlayLabels(overlay: HTMLDivElement, cy: Core, store: Map<string, HTMLDivElement>): void {
  // Remove any prior node labels (keep the edge-label child managed by ref).
  overlay.querySelectorAll('.cg-label').forEach((el) => el.remove());
  store.clear();
  cy.nodes().forEach((node) => {
    const el = document.createElement('div');
    el.className = 'cg-label' + (node.data('isFocal') === 'yes' ? ' is-focal' : '');
    el.textContent = String(node.data('name') ?? '');
    const a = recencyAlpha(node.data('updatedAt'));
    el.style.opacity = String(a);
    overlay.appendChild(el);
    store.set(node.id(), el);
  });
}

const LABEL_SCALE_MAX = 2;

/**
 * Reposition every overlay element to match Cytoscape's current rendered
 * coordinates. Called on every `render` event (pan/zoom/drag fire it too)
 * plus once after layoutstop. Labels scale 1× → 2× when the user zooms in
 * past the natural fit but never shrink below 1× so they stay readable.
 */
function positionOverlayLabels(
  cy: Core,
  store: Map<string, HTMLDivElement>,
  edgeLabelEl: HTMLDivElement | null,
  highlightedEdgeId: string | null,
  baseZoom: number,
): void {
  const ratio = cy.zoom() / (baseZoom || 1);
  const scale = Math.min(LABEL_SCALE_MAX, Math.max(1, ratio));
  document.documentElement.style.setProperty('--cnstl-label-scale', String(scale));

  cy.nodes().forEach((node) => {
    const el = store.get(node.id());
    if (!el) return;
    const p = node.renderedPosition();
    const isFocal = node.data('isFocal') === 'yes';
    const gap = (isFocal ? 14 : 10) * scale;
    el.style.left = `${p.x}px`;
    el.style.top = `${p.y + gap}px`;
  });

  if (highlightedEdgeId && edgeLabelEl) {
    const edge = cy.getElementById(highlightedEdgeId);
    if (edge && edge.length > 0) {
      const a = edge.source().renderedPosition();
      const b = edge.target().renderedPosition();
      // Biased toward source for legibility, matching the design's offset.
      const x = a.x * 0.45 + b.x * 0.55;
      const y = a.y * 0.45 + b.y * 0.55;
      const mid = edge.renderedMidpoint();
      edgeLabelEl.style.left = `${Number.isFinite(x) ? x : mid.x}px`;
      edgeLabelEl.style.top = `${Number.isFinite(y) ? y : mid.y}px`;
    }
  }
}

interface GraphViewProps {
  entityId: string | null;
  onSelectEntity?: (id: string) => void;
  /** Overrides the default viewport-relative height (`h-[calc(100vh-260px)]
   * min-h-[600px]`), which assumes GraphView owns the page (its only caller
   * before FRONT-1014). Pass a parent-relative height (e.g. `h-full
   * min-h-0`) when mounting inside a fixed-height container — otherwise the
   * graph renders taller than the space actually available and gets
   * cropped. */
  heightClassName?: string;
  /** FRONT-2489: with nothing picked, offer these as one-click starting
   *  points instead of a blank canvas. */
  suggestions?: { id: string; name: string }[];
  /**
   * FRONT-1422: draw the constellation as a picture rather than a workspace —
   * no toolbar, and no one-finger pan or pinch-zoom of the canvas itself.
   *
   * Added for the phone (`features/pwa/pwa-graph-card.tsx`), where the default
   * interactive mode is actively harmful in two ways. Cytoscape sets
   * `touch-action: none` on its container and takes over dragging, so a graph
   * sitting inside the conversation's only scroll region turns its whole area
   * into a dead zone: a vertical swipe pans the graph and the thread does not
   * move. And the toolbar's controls are raw `<button>`s, which the phone
   * stylesheet's global unlayered button reset flattens to unpadded bare text
   * (the trap already recorded in `pwa.css`'s `:where(.p-app) button` note).
   *
   * Defaults to false, so every existing caller — the Memory page and the
   * desktop chat card — keeps the full interactive graph unchanged.
   */
  staticView?: boolean;
}

interface Filters {
  types: Set<string>;
  sources: Set<string>;
  relations: Set<string>;
}

const DEFAULT_HEIGHT_CLASSNAME = 'h-[calc(100vh-260px)] min-h-[600px]';

export function GraphView({
  entityId,
  onSelectEntity,
  heightClassName,
  staticView = false,
  suggestions = [],
}: GraphViewProps) {
  const heightClass = heightClassName ?? DEFAULT_HEIGHT_CLASSNAME;
  // Callback-ref + state so the mount effect runs only when the host div
  // actually attaches. On first render we may bail to a loading/empty
  // placeholder, so a plain useRef would stay null and the mount effect
  // (with empty deps) would no-op forever, leaving Cytoscape uninstantiated.
  const [hostEl, setHostEl] = useState<HTMLDivElement | null>(null);
  const setHost = useCallback((el: HTMLDivElement | null) => setHostEl(el), []);
  const overlayRef = useRef<HTMLDivElement>(null);
  const cyRef = useRef<Core | null>(null);
  // Bumped each time a fresh cytoscape instance is created so the elements
  // effect re-runs and seeds the new instance with the current subgraph.
  const [cyTick, setCyTick] = useState(0);
  const nodeLabelElsRef = useRef<Map<string, HTMLDivElement>>(new Map());
  const edgeLabelElRef = useRef<HTMLDivElement | null>(null);
  const highlightedEdgeIdRef = useRef<string | null>(null);
  const baseZoomRef = useRef<number>(1);
  // Latest tap-callback stashed in a ref so the mount effect can stay
  // dep-less. Callers pass inline arrows that change identity every
  // render; without this, cytoscape was being torn down + rebuilt on
  // every parent render, and the elements-push effect (which depends
  // on filteredElements, not on cy) never re-ran to populate the new
  // instance — so the canvas stayed empty.
  const onSelectEntityRef = useRef(onSelectEntity);
  useEffect(() => {
    onSelectEntityRef.current = onSelectEntity;
  }, [onSelectEntity]);
  const { theme } = useTheme();
  const { data: subgraph, isLoading, error } = useGraphSubgraph(entityId);

  // Filter facets — null/empty set = include all.
  const [filters, setFilters] = useState<Filters>({
    types: new Set(),
    sources: new Set(),
    relations: new Set(),
  });

  const allFacets = useMemo(() => {
    const types = new Set<string>();
    const sources = new Set<string>();
    const relations = new Set<string>();
    if (subgraph) {
      for (const n of subgraph.nodes) {
        types.add(n.type);
        if (n.source) sources.add(n.source);
      }
      for (const e of subgraph.edges) relations.add(e.relation);
    }
    return { types, sources, relations };
  }, [subgraph]);

  const filteredElements = useMemo<ElementDefinition[] | null>(() => {
    if (!subgraph) return null;
    const focal = subgraph.focal;
    const nodeOk = (n: typeof subgraph.nodes[number]) => {
      if (n.id === focal) return true; // focal is always pinned
      if (filters.types.size > 0 && !filters.types.has(n.type)) return false;
      if (filters.sources.size > 0 && (!n.source || !filters.sources.has(n.source))) return false;
      return true;
    };
    const visibleNodes = subgraph.nodes.filter(nodeOk);
    const visibleIds = new Set(visibleNodes.map((n) => n.id));
    const visibleEdges = subgraph.edges.filter((e) => {
      if (!visibleIds.has(e.source) || !visibleIds.has(e.target)) return false;
      if (filters.relations.size > 0 && !filters.relations.has(e.relation)) return false;
      return true;
    });

    // We need the live theme tokens to colour the elements; readConstellationTheme()
    // is cheap (a few CSS var reads) and we re-run it on every theme change anyway.
    const ct = readConstellationTheme();
    return [
      ...visibleNodes.map<ElementDefinition>((n) => ({
        data: {
          id: n.id,
          name: n.name,
          type: n.type,
          color: colorForType(n.type, ct),
          alpha: recencyAlpha(n.updatedAt),
          updatedAt: n.updatedAt ?? null,
          isFocal: n.id === focal ? 'yes' : 'no',
          isPinned: n.pinned ? 'yes' : 'no',
          isFlagged: n.flagged ? 'yes' : 'no',
        },
      })),
      ...visibleEdges.map<ElementDefinition>((e, i) => ({
        data: {
          id: `e-${i}-${e.source}-${e.target}`,
          source: e.source,
          target: e.target,
          relation: e.relation,
          fresh: e.fresh ? 'yes' : 'no',
        },
      })),
    ];
  }, [subgraph, filters, theme]);

  // Mount cytoscape once with the constellation stylesheet.
  useEffect(() => {
    if (!hostEl) return;
    const ct = readConstellationTheme();
    const cy = cytoscape({
      container: hostEl,
      style: constellationStyleSpec(ct),
      wheelSensitivity: 0.2,
      minZoom: 0.15,
      maxZoom: 8,
      boxSelectionEnabled: false,
      // FRONT-1422: in `staticView` the canvas must not swallow gestures — it
      // lives inside a scrollable conversation, and cytoscape's own dragging
      // would eat the swipe that is trying to scroll the page. Tapping a node
      // still works; only panning and zooming the canvas are off.
      userPanningEnabled: !staticView,
      userZoomingEnabled: !staticView,
    });

    cy.on('tap', 'node', (e) => {
      const id = e.target.id();
      onSelectEntityRef.current?.(id);
    });

    // Edge hover: surface that edge's relation on the floating label, accent
    // the line, and bold the source node's label so direction is unambiguous
    // ("Mattias PARENT OF Elisa" — Mattias goes bold). No default label is
    // shown; the canvas is clean until the user hovers an edge.
    cy.on('mouseover', 'edge', (e) => {
      const edge = e.target;
      const relation = String(edge.data('relation') ?? '').replace(/_/g, ' ');
      if (!relation) return;
      cy.edges().removeClass('highlighted');
      edge.addClass('highlighted');
      highlightedEdgeIdRef.current = edge.id();
      const sourceId = edge.source().id();
      nodeLabelElsRef.current.forEach((el, id) => {
        el.classList.toggle('is-source-active', id === sourceId);
      });
      const el = edgeLabelElRef.current;
      if (el) {
        el.hidden = false;
        el.textContent = relation;
      }
      positionOverlayLabels(cy, nodeLabelElsRef.current, el, highlightedEdgeIdRef.current, baseZoomRef.current);
    });
    cy.on('mouseout', 'edge', () => {
      cy.edges().removeClass('highlighted');
      nodeLabelElsRef.current.forEach((el) => el.classList.remove('is-source-active'));
      highlightedEdgeIdRef.current = null;
      const el = edgeLabelElRef.current;
      if (el) el.hidden = true;
    });

    // Keep overlay labels glued to the rendered node positions on every frame
    // Cytoscape redraws. Pan/zoom/drag all fire `render` so this single handler
    // covers them. `layoutstop` arrives separately when the layout finishes.
    const reposition = () => positionOverlayLabels(cy, nodeLabelElsRef.current, edgeLabelElRef.current, highlightedEdgeIdRef.current, baseZoomRef.current);
    cy.on('render pan zoom position drag free', reposition);
    cy.on('layoutstop', () => {
      cy.fit(undefined, 60);
      baseZoomRef.current = cy.zoom() || 1;
      reposition();
    });

    // The host can mount with the wrong size (tab pane just becoming visible,
    // sidebar still settling). Track size changes and refit so the viewport
    // always matches what the user sees — otherwise nodes can land offscreen.
    const ro = new ResizeObserver(() => {
      cy.resize();
      if (cy.elements().length > 0) {
        cy.fit(undefined, 60);
        baseZoomRef.current = cy.zoom() || 1;
        reposition();
      }
    });
    ro.observe(hostEl);

    cyRef.current = cy;
    setCyTick((t) => t + 1);
    return () => {
      ro.disconnect();
      cy.destroy();
      cyRef.current = null;
    };
  }, [hostEl]);

  // React to theme changes: re-read CSS tokens and push a fresh stylesheet.
  // Node colours are stored on each element's `data` and refreshed in the
  // element builder via the `theme` dependency above, so this only needs to
  // push the new stylesheet (which controls halos + edges + accents).
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) return;
    const ct = readConstellationTheme();
    cy.style().fromJson(constellationStyleSpec(ct)).update();
  }, [theme]);

  // Push elements + relayout whenever the filtered set changes. Also rebuild
  // the HTML overlay labels and pick a highlighted focal-incident edge.
  useEffect(() => {
    const cy = cyRef.current;
    const overlay = overlayRef.current;
    if (!cy || overlay == null || !filteredElements) return;

    cy.batch(() => {
      cy.elements().remove();
      cy.add(filteredElements);
    });
    // Concentric ring layout: focal at center, neighbours on a ring around it.
    // For larger neighbourhoods we fan out across two rings so labels don't crowd.
    const neighbourCount = filteredElements.reduce((acc, el) => {
      const data = el.data as { isFocal?: string; source?: unknown };
      return !('source' in data) && data.isFocal === 'no' ? acc + 1 : acc;
    }, 0);
    const useMultipleRings = neighbourCount > 20;
    cy.resize();
    cy.layout({
      name: 'concentric',
      animate: true,
      animationDuration: 400,
      fit: true,
      padding: 60,
      equidistant: true,
      minNodeSpacing: 50,
      spacingFactor: 1.6,
      avoidOverlap: true,
      startAngle: -Math.PI / 2,
      concentric: (node: NodeSingular) => {
        if (node.data('isFocal') === 'yes') return 2;
        if (!useMultipleRings) return 0;
        const id = node.id();
        let hash = 0;
        for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) & 0xffffffff;
        return Math.abs(hash) % 2 === 0 ? 0 : 1;
      },
      levelWidth: () => 1,
    } as never).run();

    // Rebuild per-node serif labels. Edge relations are surfaced on hover
    // only — initialize the floating edge label hidden.
    rebuildOverlayLabels(overlay, cy, nodeLabelElsRef.current);
    highlightedEdgeIdRef.current = null;
    if (edgeLabelElRef.current) edgeLabelElRef.current.hidden = true;
  }, [filteredElements, subgraph, cyTick]);

  if (!entityId) {
    return (
      <div className={`${heightClass} rounded-lg border border-border bg-muted/30 flex flex-col items-center justify-center gap-3 px-6 text-center`}>
        <p className="text-sm font-medium text-foreground">See how anything EDDY knows connects</p>
        <p className="max-w-md text-sm text-muted-foreground text-pretty">
          Search above, or start from one of these — the graph draws it in the middle with everything linked to it around it.
        </p>
        {suggestions.length > 0 && onSelectEntity && (
          <div className="flex max-w-2xl flex-wrap justify-center gap-2">
            {suggestions.slice(0, 8).map((s) => (
              <button
                key={s.id}
                type="button"
                data-graph-suggestion={s.id}
                onClick={() => onSelectEntity(s.id)}
                className="rounded-full border border-border bg-background px-3 py-1 text-xs text-foreground hover:bg-accent"
              >
                {s.name}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }
  if (isLoading) {
    return (
      <div className={`${heightClass} rounded-lg border border-border bg-muted/30 flex items-center justify-center text-sm text-muted-foreground`}>
        Loading graph…
      </div>
    );
  }
  if (error) {
    return (
      <div className={`${heightClass} rounded-lg border border-destructive/40 bg-destructive/5 flex items-center justify-center text-sm text-destructive`}>
        Failed to load graph: {(error as Error).message}
      </div>
    );
  }
  if (!subgraph || subgraph.nodes.length === 0) {
    return (
      <div className={`${heightClass} rounded-lg border border-border bg-muted/30 flex items-center justify-center text-sm text-muted-foreground`}>
        No connections found
      </div>
    );
  }

  const activeFilterCount =
    filters.types.size + filters.sources.size + filters.relations.size;

  function clearAllFilters() {
    setFilters({ types: new Set(), sources: new Set(), relations: new Set() });
  }

  // A plain `space-y-2` wrapper (auto height) works for the default,
  // viewport-relative height class — the graph div self-sizes off `vh`, so
  // nothing above it needs a defined height. An embedded caller's
  // `heightClass` is parent-relative instead (e.g. `h-full`), which only
  // resolves against a wrapper that itself participates in the ancestor's
  // flex chain — so the embedded case switches the wrapper to a flex column
  // and gives the graph its remaining space via `flex-1` rather than a
  // fixed height.
  const isEmbedded = heightClassName != null;

  return (
    <div className={isEmbedded ? 'flex h-full min-h-0 flex-col gap-2' : 'space-y-2'}>
      {/* FRONT-1422: `staticView` drops the toolbar entirely — see the prop's
       * doc. Its controls are raw `<button>`s, which the phone stylesheet's
       * global button reset strips of all padding and background, and with
       * panning and zooming off there is nothing left for them to drive. */}
      {!staticView && (
      <GraphToolbar
        facets={allFacets}
        filters={filters}
        onChange={setFilters}
        onFit={() => cyRef.current?.fit(undefined, 40)}
        onZoomIn={() => {
          const cy = cyRef.current;
          if (!cy) return;
          cy.zoom({ level: cy.zoom() * 1.25, renderedPosition: { x: cy.width() / 2, y: cy.height() / 2 } });
        }}
        onZoomOut={() => {
          const cy = cyRef.current;
          if (!cy) return;
          cy.zoom({ level: cy.zoom() / 1.25, renderedPosition: { x: cy.width() / 2, y: cy.height() / 2 } });
        }}
        onCenterFocal={() => {
          const cy = cyRef.current;
          if (!cy || !subgraph?.focal) return;
          const node = cy.getElementById(subgraph.focal);
          if (node.nonempty()) cy.center(node);
        }}
      />
      )}
      {!staticView && activeFilterCount > 0 && (
        <ActiveFilterChips filters={filters} onChange={setFilters} onClear={clearAllFilters} />
      )}
      <div
        className={`constellation-graph relative w-full ${isEmbedded ? 'flex-1 min-h-0' : heightClass} rounded-lg border border-border overflow-hidden`}
      >
        <div ref={setHost} className="cg-host absolute inset-0" />
        <div ref={overlayRef} className="cg-overlay">
          <div ref={edgeLabelElRef} className="cg-edge-label" hidden />
        </div>
        <div className="absolute bottom-2 left-2 right-2 flex flex-wrap gap-3 text-[length:var(--text-2xs)] uppercase tracking-wider z-10" style={{ color: 'var(--cnstl-label-muted)' }}>
          {[...allFacets.types].sort().map((t) => (
            <span key={t} className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: `var(--c-${t}, var(--c-routine))` }} />
              <span className="italic" style={{ fontFamily: 'var(--cnstl-serif)' }}>{t}</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Toolbar above the graph. Left cluster = filter dropdowns (Types / Sources
 * / Relations), each backed by a checkbox popover. Right cluster = viewport
 * controls (zoom in/out, center focal, fit). Active filters get a count
 * badge; the dropdown menu surfaces with the same dot colors used in the
 * graph itself for the Types facet so the link is obvious.
 *
 * Pill chips for currently-active filters render in a separate row below
 * via <ActiveFilterChips> — keeps the toolbar's height stable regardless
 * of how many facets are toggled on.
 */
function GraphToolbar({
  facets,
  filters,
  onChange,
  onFit,
  onZoomIn,
  onZoomOut,
  onCenterFocal,
}: {
  facets: { types: Set<string>; sources: Set<string>; relations: Set<string> };
  filters: Filters;
  onChange: (f: Filters) => void;
  onFit: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onCenterFocal: () => void;
}) {
  return (
    <div className="flex items-center gap-1 rounded-md border border-border bg-card px-1.5 py-1">
      <FacetDropdown
        label="Types"
        items={[...facets.types].sort()}
        active={filters.types}
        renderItem={(v) => (
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full shrink-0" style={{ background: `var(--c-${v}, var(--c-routine))` }} />
            {v}
          </span>
        )}
        onChange={(next) => onChange({ ...filters, types: next })}
      />
      <FacetDropdown
        label="Sources"
        items={[...facets.sources].sort()}
        active={filters.sources}
        onChange={(next) => onChange({ ...filters, sources: next })}
      />
      <FacetDropdown
        label="Relations"
        items={[...facets.relations].sort()}
        active={filters.relations}
        onChange={(next) => onChange({ ...filters, relations: next })}
      />

      <div className="flex-1" />

      <ToolbarIconButton title="Zoom out" onClick={onZoomOut}>
        <MagnifyingGlassMinus size={14} />
      </ToolbarIconButton>
      <ToolbarIconButton title="Zoom in" onClick={onZoomIn}>
        <MagnifyingGlassPlus size={14} />
      </ToolbarIconButton>
      <ToolbarIconButton title="Center on focal entity" onClick={onCenterFocal}>
        <Target size={14} />
      </ToolbarIconButton>
      <ToolbarIconButton title="Fit graph to view" onClick={onFit}>
        <FrameCorners size={14} />
      </ToolbarIconButton>
    </div>
  );
}

function ToolbarIconButton({
  children,
  title,
  onClick,
}: {
  children: React.ReactNode;
  title: string;
  onClick: () => void;
}) {
  return (
    <Tip label={title}>
      <button
        type="button"
        onClick={onClick}
        className="inline-flex items-center justify-center w-7 h-7 rounded text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
      >
        {children}
      </button>
    </Tip>
  );
}

function FacetDropdown({
  label,
  items,
  active,
  onChange,
  renderItem,
}: {
  label: string;
  items: string[];
  active: Set<string>;
  onChange: (next: Set<string>) => void;
  renderItem?: (v: string) => React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [open]);

  if (items.length === 0) return null;

  const toggle = (v: string) => {
    const next = new Set(active);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    onChange(next);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1.5 rounded px-2 py-1 text-xs font-medium transition-colors ${
          active.size > 0
            ? 'bg-primary/15 text-primary hover:bg-primary/20'
            : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
        }`}
      >
        {label}
        {active.size > 0 && (
          <span className="text-[length:var(--text-2xs)] font-semibold tabular-nums bg-primary/25 text-primary rounded-full px-1.5 leading-tight">
            {active.size}
          </span>
        )}
        <CaretDown size={10} weight="bold" className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 min-w-[180px] max-h-72 overflow-y-auto rounded-md border border-border bg-popover shadow-lg z-50 py-1">
          {active.size > 0 && (
            <button
              type="button"
              onClick={() => onChange(new Set())}
              className="w-full text-left px-3 py-1.5 text-[length:var(--text-2xs)] text-muted-foreground hover:text-foreground hover:bg-muted/50 border-b border-border"
            >
              Clear {active.size} selected
            </button>
          )}
          {items.map((v) => {
            const on = active.has(v);
            return (
              <button
                key={v}
                type="button"
                onClick={() => toggle(v)}
                className="w-full text-left px-3 py-1.5 text-xs hover:bg-muted/50 flex items-center gap-2"
              >
                <span
                  className={`shrink-0 w-3 h-3 rounded border flex items-center justify-center ${
                    on ? 'bg-primary border-primary' : 'border-border bg-card'
                  }`}
                >
                  {on && (
                    <svg viewBox="0 0 16 16" className="w-2.5 h-2.5 text-primary-foreground">
                      <path
                        d="M3 8.5l3 3 7-7"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </span>
                {renderItem ? renderItem(v) : <span className="truncate">{v}</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ActiveFilterChips({
  filters,
  onChange,
  onClear,
}: {
  filters: Filters;
  onChange: (f: Filters) => void;
  onClear: () => void;
}) {
  const remove = <K extends keyof Filters>(key: K, value: string) => {
    const next = new Set(filters[key]);
    next.delete(value);
    onChange({ ...filters, [key]: next });
  };

  const allChips: Array<{ key: keyof Filters; value: string; label: string; color?: string }> = [];
  filters.types.forEach((v) => allChips.push({ key: 'types', value: v, label: v, color: `var(--c-${v}, var(--c-routine))` }));
  filters.sources.forEach((v) => allChips.push({ key: 'sources', value: v, label: v }));
  filters.relations.forEach((v) => allChips.push({ key: 'relations', value: v, label: v }));

  return (
    <div className="flex flex-wrap items-center gap-1.5 text-[length:var(--text-2xs)]">
      {allChips.map((chip) => (
        <span
          key={`${chip.key}:${chip.value}`}
          className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary border border-primary/30 pl-1.5 pr-1 py-0.5"
        >
          {chip.color && (
            <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: chip.color }} />
          )}
          <span>{chip.label}</span>
          <Tip label={`Remove ${chip.label}`}>
            <button
              type="button"
              onClick={() => remove(chip.key, chip.value)}
              className="hover:bg-primary/20 rounded-full p-0.5"
            >
              <X size={9} weight="bold" />
            </button>
          </Tip>
        </span>
      ))}
      <button
        type="button"
        onClick={onClear}
        className="text-[length:var(--text-2xs)] text-muted-foreground hover:text-foreground underline ml-1"
      >
        Clear all
      </button>
    </div>
  );
}
